export const TRACK_SIZE = 68;
export const RING_LENGTH = 64;
export const HOME_LANE_LENGTH = 7;
export const GOAL_PROGRESS = RING_LENGTH + HOME_LANE_LENGTH; // 71
export const PIECES_PER_PLAYER = 4;

export const SEAT_COLORS = ['coral', 'amber', 'jade', 'azure'] as const;
export type SeatColor = (typeof SEAT_COLORS)[number];

export const AVATAR_IDS = [
  'aurora',
  'volt',
  'moss',
  'lumen',
  'kite',
  'forge',
  'nyx',
  'tide',
  'ember',
  'pixel',
  'solstice',
  'oracle',
] as const;
export type AvatarId = (typeof AVATAR_IDS)[number];

export const DICE_SKINS = ['classic', 'glass', 'fire', 'tech', 'aurora'] as const;
export type DiceSkin = (typeof DICE_SKINS)[number];

export const GAME_MODES = ['classic', 'adventure'] as const;
export type GameMode = (typeof GAME_MODES)[number];

export type Visibility = 'public' | 'private';
export type RoomStatus = 'lobby' | 'playing' | 'finished';
export type GamePhase = 'awaitingRoll' | 'awaitingMove' | 'finished';
export type PieceStatus = 'home' | 'active' | 'goal';

export interface AvatarDefinition {
  id: AvatarId;
  name: string;
  kind: 'android' | 'mech' | 'synth' | 'drone' | 'hologram' | 'bio' | 'hydro' | 'beast';
  tagline: string;
  serial: string;
  premium?: ProductKey;
  palette: {
    skin: string;
    accent: string;
    deep: string;
    glow: string;
  };
}

export interface DiceDefinition {
  id: DiceSkin;
  name: string;
  description: string;
  premium?: ProductKey;
}

export type ProductKey = 'voyager';

export const PACK_PRICE_LABEL = '1,99 € · pago único · sin suscripción';

export const AI_MATCH_PASS = {
  key: 'AI_MATCH_PASS',
  name: 'Pase de partida con agentes IA',
  priceLabel: '1,99 €',
  maxAgents: 2,
  maxTurnsPerAgent: 12,
} as const;

export type CosmeticKey =
  | 'profile-frame-astral'
  | 'dice-effect-astral'
  | 'capture-effect-astral'
  | 'badge-first-explorer';

export interface CosmeticDefinition {
  id: CosmeticKey;
  name: string;
  description: string;
  kind: 'frame' | 'dice-effect' | 'capture-effect' | 'badge';
}

export const COSMETICS: Record<CosmeticKey, CosmeticDefinition> = {
  'profile-frame-astral': {
    id: 'profile-frame-astral',
    name: 'Marco de perfil Astral',
    description: 'Marco holográfico exclusivo para tu pasaporte.',
    kind: 'frame',
  },
  'dice-effect-astral': {
    id: 'dice-effect-astral',
    name: 'Efecto de dado Astral',
    description: 'Estela de energía al tirar el dado. Solo visual.',
    kind: 'dice-effect',
  },
  'capture-effect-astral': {
    id: 'capture-effect-astral',
    name: 'Efecto de captura Astral',
    description: 'Aura adicional en la escena de captura. Solo visual.',
    kind: 'capture-effect',
  },
  'badge-first-explorer': {
    id: 'badge-first-explorer',
    name: 'Insignia Primer explorador',
    description: 'Distintivo de perfil para quien apoyó la primera edición.',
    kind: 'badge',
  },
};

export const PRODUCT_COSMETICS: CosmeticKey[] = [
  'profile-frame-astral',
  'dice-effect-astral',
  'capture-effect-astral',
  'badge-first-explorer',
];

export const PRODUCTS: Record<ProductKey, { name: string; shortName: string; description: string; includes: string[] }> = {
  voyager: {
    name: 'Reino de Tableros — Pack Cosmético',
    shortName: 'Pack Cosmético',
    description: 'Un pack cosmético opcional para apoyar el proyecto. No da ventajas de juego.',
    includes: [
      'Avatar Solstice',
      'Avatar Oráculo',
      'Dado Astral',
      'Marco de perfil exclusivo',
      'Efecto visual al tirar el dado',
      'Efecto visual al capturar una ficha',
      'Insignia Primer explorador',
    ],
  },
};

export const DICE_CATALOG: DiceDefinition[] = [
  { id: 'classic', name: 'Obsidiana', description: 'Cristal negro y pips de cuarzo.' },
  { id: 'glass', name: 'Prisma', description: 'Cristal holográfico con reflejos de escarcha.' },
  { id: 'fire', name: 'Plasma', description: 'Núcleo de plasma rosa y pips incandescentes.' },
  { id: 'tech', name: 'Circuito', description: 'Aleación de zafiro y runas de neón.' },
  { id: 'aurora', name: 'Astral', description: 'Degradado de fase exclusivo del Pack Cosmético.', premium: 'voyager' },
];

export const AVATAR_CATALOG: AvatarDefinition[] = [
  {
    id: 'aurora',
    name: 'Aurora-7',
    kind: 'android',
    tagline: 'Unidad de asalto solar',
    serial: 'AX-07 / SOL',
    palette: { skin: '#d7e6f5', accent: '#5ef2ff', deep: '#0a2542', glow: '#a8f6ff' },
  },
  {
    id: 'volt',
    name: 'Volt',
    kind: 'mech',
    tagline: 'Núcleo de chispa',
    serial: 'VT-11 / ION',
    palette: { skin: '#a9c3d6', accent: '#5effc8', deep: '#082d35', glow: '#82ffe1' },
  },
  {
    id: 'moss',
    name: 'Moss-3',
    kind: 'bio',
    tagline: 'Simbionte de jardín',
    serial: 'MS-03 / BIO',
    palette: { skin: '#a9d6b0', accent: '#8dff8f', deep: '#0b3022', glow: '#bcff9a' },
  },
  {
    id: 'lumen',
    name: 'Lumen',
    kind: 'hologram',
    tagline: 'Proyección estelar',
    serial: 'LM-∞ / LUX',
    palette: { skin: '#cdd9ff', accent: '#c59aff', deep: '#211045', glow: '#e4c8ff' },
  },
  {
    id: 'kite',
    name: 'Kite',
    kind: 'drone',
    tagline: 'Explorador de viento',
    serial: 'KT-05 / AIR',
    palette: { skin: '#c9e1eb', accent: '#ffc46b', deep: '#2c1c0b', glow: '#ffe0a0' },
  },
  {
    id: 'forge',
    name: 'Forge',
    kind: 'mech',
    tagline: 'Forja de titanio',
    serial: 'FG-09 / Fe',
    palette: { skin: '#c7b7a8', accent: '#ff9c63', deep: '#33150e', glow: '#ffbf80' },
  },
  {
    id: 'nyx',
    name: 'Nyx-9',
    kind: 'synth',
    tagline: 'Espectro de datos',
    serial: 'NX-09 / VOID',
    palette: { skin: '#a4a6d6', accent: '#a786ff', deep: '#15102e', glow: '#c2a9ff' },
  },
  {
    id: 'tide',
    name: 'Tide',
    kind: 'hydro',
    tagline: 'Marea cuántica',
    serial: 'TD-04 / H2O',
    palette: { skin: '#a7d8e3', accent: '#5adfff', deep: '#062c3d', glow: '#8ceeff' },
  },
  {
    id: 'ember',
    name: 'Ember',
    kind: 'beast',
    tagline: 'Dragón de plasma',
    serial: 'EM-13 / PLS',
    palette: { skin: '#d9a294', accent: '#ff7c61', deep: '#3c0f17', glow: '#ffb07e' },
  },
  {
    id: 'pixel',
    name: 'Pixel',
    kind: 'drone',
    tagline: 'Compañero de bolsillo',
    serial: 'PX-02 / BIT',
    palette: { skin: '#d5e0e6', accent: '#a5ff5f', deep: '#172c16', glow: '#c8ff7a' },
  },
  {
    id: 'solstice',
    name: 'Solstice',
    kind: 'android',
    tagline: 'Lanza del amanecer',
    serial: 'SL-01 / SOL+',
    premium: 'voyager',
    palette: { skin: '#ffe0c4', accent: '#ffd15b', deep: '#3b1d16', glow: '#ffb1d9' },
  },
  {
    id: 'oracle',
    name: 'Oracle',
    kind: 'hologram',
    tagline: 'Lector de constelaciones',
    serial: 'OR-00 / QBT',
    premium: 'voyager',
    palette: { skin: '#d3e5ff', accent: '#8ddcff', deep: '#111d3e', glow: '#d7f3ff' },
  },
];

export const AVATARS_BY_ID = Object.fromEntries(AVATAR_CATALOG.map((avatar) => [avatar.id, avatar])) as Record<
  AvatarId,
  AvatarDefinition
>;

export const PRODUCT_AVATARS: AvatarId[] = ['solstice', 'oracle'];
export const PRODUCT_DICE: DiceSkin[] = ['aurora'];

export interface PlayerProfile {
  name: string;
  avatar: AvatarId;
  diceSkin: DiceSkin;
}

export interface GameSettings {
  mode: GameMode;
  effectsEnabled: boolean;
  maxPlayers: 2 | 3 | 4;
  visibility: Visibility;
}

export type SeatKind = 'human' | 'agent';
export type AgentProvider = 'openai' | 'deepseek';

export interface HumanSeat {
  kind: 'human';
}

export interface AgentSeat {
  kind: 'agent';
  provider: AgentProvider;
  model: string;
  displayName: string;
}

export type SeatConfig = HumanSeat | AgentSeat;

export interface RoomMember extends PlayerProfile {
  id: string;
  /** Compatibilidad con los bots existentes. Un agente puede seguir usando este flag. */
  isBot: boolean;
  /** Tipo explícito de asiento; opcional para no romper salas antiguas. */
  seat?: SeatConfig;
  connected: boolean;
  joinedAt: number;
}

export interface RoomView {
  code: string;
  hostId: string;
  settings: GameSettings;
  status: RoomStatus;
  members: RoomMember[];
  game: GameState | null;
  createdAt: number;
  quickMatch: boolean;
}

export interface PublicRoomSummary {
  code: string;
  hostName: string;
  mode: GameMode;
  effectsEnabled: boolean;
  maxPlayers: number;
  playerCount: number;
  humanCount: number;
  createdAt: number;
}

export interface PieceState {
  id: string;
  color: SeatColor;
  slot: number;
  progress: number;
  status: PieceStatus;
  shielded: boolean;
}

export interface GamePlayer {
  id: string;
  name: string;
  avatar: AvatarId;
  diceSkin: DiceSkin;
  color: SeatColor;
  /** Compatibilidad con los bots existentes. */
  isBot: boolean;
  /** Tipo explícito de asiento; opcional para no alterar partidas existentes. */
  seat?: SeatConfig;
  connected: boolean;
  pieces: PieceState[];
  score: number;
  skipNextTurn: boolean;
  forfeited: boolean;
}

export type CellKind = 'normal' | 'start' | 'safe' | 'shortcut' | 'event' | 'risk';
export type EffectKind = 'shortcut' | 'risk' | 'event' | 'advance' | 'retreat' | 'shield' | 'wait' | 'extraTurn';

export interface CellInfo {
  index: number;
  kind: CellKind;
  color?: SeatColor;
}

export interface EffectPreview {
  kind: CellKind;
  title: string;
  description: string;
  icon: string;
}

export interface LegalMove {
  pieceId: string;
  kind: 'exit' | 'advance';
  from: number;
  to: number;
  captures: number;
  trackIndex: number | null;
  effectPreview: EffectPreview | null;
}

export type GameLogKind =
  | 'roll'
  | 'move'
  | 'capture'
  | 'effect'
  | 'turn'
  | 'win'
  | 'system'
  | 'shield'
  | 'skip'
  | 'penalty';

export interface GameLogEntry {
  id: string;
  at: number;
  kind: GameLogKind;
  text: string;
  playerId?: string;
  color?: SeatColor;
}

export interface GameState {
  id: string;
  mode: GameMode;
  effectsEnabled: boolean;
  players: GamePlayer[];
  phase: GamePhase;
  currentPlayerId: string;
  dice: number | null;
  legalMoves: LegalMove[];
  consecutiveSixes: number;
  turnNumber: number;
  winnerId: string | null;
  log: GameLogEntry[];
  logSeq: number;
  createdAt: number;
  updatedAt: number;
  rematch: number;
}

export interface EffectPresentation {
  kind: EffectKind | 'capture' | 'skip' | 'penalty' | 'win' | 'extra';
  title: string;
  description: string;
  icon: string;
  color?: SeatColor;
  from?: number;
  to?: number;
}

export interface CapturedPieceAnimation {
  pieceId: string;
  color: SeatColor;
  progress: number;
}

export interface GameAnimation {
  kind: 'portal' | 'crack' | 'capture' | 'summon' | 'goal' | 'shield' | 'wait' | 'extra' | 'rune';
  color: SeatColor;
  pieceId?: string;
  from?: number;
  to?: number;
  trackIndex?: number;
  captured?: CapturedPieceAnimation[];
  reward?: number;
}

export interface GameAction {
  id: string;
  type: 'roll' | 'move' | 'pass' | 'penalty' | 'system';
  playerId?: string;
  dice?: number;
  pieceId?: string;
  message: string;
  effect?: EffectPresentation;
  animation?: GameAnimation;
  captures?: { playerId: string; color: SeatColor; count: number }[];
  reward?: number;
  extraTurn?: boolean;
  at: number;
}

export interface SessionReady {
  playerId: string;
  token: string;
  isNew: boolean;
}

export interface CheckoutAttribution {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
}

export interface AiMatchPassStatus {
  enabled: boolean;
  isHost: boolean;
  status: 'disabled' | 'available' | 'active' | 'expired' | 'exhausted';
  priceLabel: string;
  maxAgents: number;
  maxTurnsPerAgent: number;
  expiresAt?: number;
  message: string;
}

export interface AppConfig {
  paymentsEnabled: boolean;
  priceLabel: string;
  productName: string;
  productKey: ProductKey;
  publicUrl: string;
  rulesVersion: number;
  aiMatchPassEnabled: boolean;
  aiMatchPassTestPurchaseEnabled: boolean;
  aiMatchPassCheckoutEnabled: boolean;
  aiMatchPassPriceLabel: string;
  aiMatchPassStripeKeyValid: boolean;
  aiMatchPassStripePriceConfigured: boolean;
  aiMatchPassStripeWebhookConfigured: boolean;
  aiMatchPassStripePriceReadable: boolean;
  aiMatchPassStripeCheckoutCreatable: boolean;
  availableAgentProviders: AgentProvider[];
}

export interface EntitlementsView {
  products: ProductKey[];
  premiumAvatars: AvatarId[];
  premiumDice: DiceSkin[];
  cosmetics: CosmeticKey[];
}

export type SocketAck = { ok: true; message?: string } | { ok: false; error: string; code?: string };

export interface ServerToClientEvents {
  'session:ready': (payload: SessionReady) => void;
  'room:state': (room: RoomView | null) => void;
  'lobby:rooms': (rooms: PublicRoomSummary[]) => void;
  'game:action': (action: GameAction) => void;
  'notice': (payload: { tone: 'info' | 'success' | 'warning' | 'error'; message: string }) => void;
  'entitlements:update': (payload: EntitlementsView) => void;
}

export interface ClientToServerEvents {
  'profile:update': (profile: PlayerProfile, ack: (result: SocketAck) => void) => void;
  'room:create': (
    settings: Pick<GameSettings, 'mode' | 'effectsEnabled' | 'maxPlayers' | 'visibility'>,
    ack: (result: SocketAck) => void,
  ) => void;
  'room:quick': (ack: (result: SocketAck) => void) => void;
  'room:join': (payload: { code: string }, ack: (result: SocketAck) => void) => void;
  'room:addBot': (ack: (result: SocketAck) => void) => void;
  'room:removeBot': (payload: { playerId: string }, ack: (result: SocketAck) => void) => void;
  'room:addAgent': (payload: { provider?: AgentProvider } | undefined, ack: (result: SocketAck) => void) => void;
  'room:removeAgent': (payload: { playerId: string }, ack: (result: SocketAck) => void) => void;
  'room:settings': (patch: Partial<GameSettings>, ack: (result: SocketAck) => void) => void;
  'room:start': (ack: (result: SocketAck) => void) => void;
  'room:leave': (ack: (result: SocketAck) => void) => void;
  'room:rematch': (ack: (result: SocketAck) => void) => void;
  'game:roll': (ack: (result: SocketAck) => void) => void;
  'game:move': (payload: { pieceId: string }, ack: (result: SocketAck) => void) => void;
  'game:forfeit': (ack: (result: SocketAck) => void) => void;
  'room:rejoin': (ack: (result: SocketAck) => void) => void;
  'lobby:request': () => void;
}
